
import React from "react";
import { useNavigate } from "react-router-dom";
import {
    ArrowRight,
    MapPin,
    MoveUpRight,
} from "lucide-react";

const destinations = [
    {
        id: 1,
        name: "Downtown Dubai",
        subtitle: "Iconic Skyline & City Attractions",
        tag: "Most Popular",
        image:
            "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=85",
        area: "Downtown Dubai",
    },
    {
        id: 2,
        name: "Palm Jumeirah",
        subtitle: "Luxury Island Escapes",
        tag: "Beach & Resorts",
        image:
            "https://images.unsplash.com/photo-1546412414-e1885259563a?w=900&q=85",
        area: "Palm Jumeirah",
    },
    {
        id: 3,
        name: "Dubai Marina",
        subtitle: "Waterfront Living",
        tag: "Waterfront",
        image:
            "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=900&q=85",
        area: "Dubai Marina",
    },
    {
        id: 4,
        name: "Jumeirah Beach",
        subtitle: "Sun, Sand & Sea",
        tag: "Beachside",
        image:
            "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=900&q=85",
        area: "Jumeirah",
    },
    {
        id: 5,
        name: "Dubai Creek",
        subtitle: "Culture & Heritage",
        tag: "Old Dubai",
        image:
            "https://images.unsplash.com/photo-1526495124232-a04e1849168c?w=900&q=85",
        area: "Deira",
    },
];

function DestinationCard({
    destination,
    featured = false,
    onClick,
}) {
    return (
        <article
            className={`
        group relative isolate w-full
        overflow-hidden rounded-[16px]
        bg-[#064E3B]
        shadow-[0_6px_20px_rgba(6,78,59,0.10)]
        transition-all duration-300
        hover:-translate-y-1
        hover:shadow-[0_16px_35px_rgba(6,78,59,0.20)]
        ${featured
                    ? "h-[360px] lg:h-[440px]"
                    : "h-[210px] lg:h-[212px]"
                }
      `}
        >
            {/* IMAGE */}
            <img
                src={destination.image}
                alt={destination.name}
                loading="lazy"
                className="
          absolute inset-0 h-full w-full
          object-cover
          transition-transform duration-700
          group-hover:scale-110
        "
            />

            {/* EMERALD OVERLAY */}
            <div
                className="
          pointer-events-none absolute inset-0
          bg-gradient-to-t
          from-[#032F22]/95
          via-[#064E3B]/30
          to-[#064E3B]/10
        "
            />

            {/* CLICKABLE AREA */}
            <button
                type="button"
                onClick={() => onClick(destination)}
                aria-label={`Explore hotels in ${destination.name}`}
                className="
          absolute inset-0 z-10
          cursor-pointer rounded-[16px]
          focus-visible:outline-2
          focus-visible:outline-offset-2
          focus-visible:outline-[#168B43]
        "
            />

            {/* DESTINATION TAG */}
            <span
                className="
          pointer-events-none absolute left-4 top-4
          rounded-full border border-white/30
          bg-white/90 px-3 py-1.5
          text-[10px] font-bold text-[#064E3B]
          backdrop-blur-sm
        "
            >
                {destination.tag}
            </span>

            {/* CONTENT */}
            <div
                className={`
          pointer-events-none absolute
          inset-x-0 bottom-0
          ${featured ? "p-6 sm:p-7" : "p-4 sm:p-5"}
        `}
            >
                <div className="mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-[#A7F3C3]">
                    <MapPin size={12} />
                    Dubai, UAE
                </div>

                <div className="flex items-end justify-between gap-3">
                    <div>
                        <h3
                            className={`
                font-extrabold leading-tight
                tracking-[-0.03em] text-white
                ${featured
                                    ? "text-[27px] sm:text-[32px]"
                                    : "text-[18px] sm:text-[20px]"
                                }
              `}
                        >
                            {destination.name}
                        </h3>

                        <p
                            className="
                mt-1.5 text-[11px]
                text-white/85 sm:text-xs
              "
                        >
                            {destination.subtitle}
                        </p>

                        {featured && (
                            <div className="mt-4 flex items-center gap-2 text-xs font-bold text-[#86E5A2]">
                                Explore Destination
                                <ArrowRight size={15} />
                            </div>
                        )}
                    </div>

                    <span
                        className="
              flex h-9 w-9 shrink-0
              items-center justify-center
              rounded-full bg-[#168B43]
              text-white
              transition-all duration-300
              group-hover:scale-110
              group-hover:bg-[#20A451]
            "
                    >
                        <MoveUpRight size={18} />
                    </span>
                </div>
            </div>
        </article>
    );
}

export default function PopularDestinations() {
    const navigate = useNavigate();

    const handleDestinationClick = (destination) => {
        navigate("/search-hotels", {
            state: {
                destination: "Dubai",
                area: destination.area,
                location: destination.area,
                checkIn: "",
                checkOut: "",
                guests: 2,
                rooms: 1,
            },
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    };

    return (
        <section
            id="popular-destinations"
            className="bg-[#F5FAF7] py-12 sm:py-14 lg:py-16"
        >
            {/* SAME WIDTH AS HEADER */}
            <div
                className="
          mx-auto w-full max-w-[1536px]
          px-4 sm:px-6 lg:px-8
        "
            >
                {/* SECTION HEADER */}
                <div
                    className="
            mb-7 flex flex-col justify-between gap-4
            sm:flex-row sm:items-center
          "
                >
                    <div>
                        <p
                            className="
                mb-1 text-[10px] font-extrabold
                uppercase tracking-[0.2em]
                text-[#168B43]
              "
                        >
                            Discover More
                        </p>

                        <h2
                            className="
                text-[27px] font-extrabold
                leading-tight tracking-[-0.04em]
                text-[#17324D]
                sm:text-[32px]
              "
                        >
                            Popular Destinations{" "}
                            <span className="text-[#168B43]">
                                in Dubai
                            </span>
                        </h2>

                        <p
                            className="
                mt-1.5 max-w-[760px]
                text-[12px] leading-5
                text-[#64748B]
                sm:text-[13px]
              "
                        >
                            Explore Dubai's iconic destinations, from
                            breathtaking skylines to beautiful beaches
                            and cultural landmarks.
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => navigate("/hotels")}
                        className="
              group inline-flex h-9 shrink-0
              items-center justify-center gap-2
              self-start rounded-full
              border border-[#9AB9A8]
              bg-white px-4
              text-[11px] font-bold text-[#17324D]
              transition-all duration-200
              hover:border-[#168B43]
              hover:bg-[#E8F7EE]
              sm:self-center
            "
                    >
                        Explore All
                        <ArrowRight
                            size={14}
                            className="
                transition-transform
                group-hover:translate-x-1
              "
                        />
                    </button>
                </div>

                {/* DESTINATIONS GRID */}
                <div
                    className="
            grid grid-cols-1 gap-4
            lg:grid-cols-2
          "
                >
                    {/* LARGE FEATURED DESTINATION */}
                    <DestinationCard
                        destination={destinations[0]}
                        featured
                        onClick={handleDestinationClick}
                    />

                    {/* FOUR SMALL DESTINATIONS */}
                    <div
                        className="
              grid grid-cols-1 gap-4
              sm:grid-cols-2
            "
                    >
                        {destinations.slice(1).map((destination) => (
                            <DestinationCard
                                key={destination.id}
                                destination={destination}
                                onClick={handleDestinationClick}
                            />
                        ))}
                    </div>
                </div>

                {/* BOTTOM TEXT */}
                <div className="mt-8 flex items-center justify-center gap-2 text-center text-[12px] text-[#64748B]">
                    <MapPin
                        size={15}
                        className="text-[#168B43]"
                    />
                    Your next Dubai adventure starts with the perfect stay.
                </div>
            </div>
        </section>
    );
}
